import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Format date for display
export function formatDate(date: Date | string): string {
  const d = new Date(date)
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

// Format time for display
export function formatTime(date: Date | string): string {
  const d = new Date(date)
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
}

// Format currency
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD'
  }).format(amount)
}

// Check if date is today
export function isToday(date: Date | string): boolean {
  const today = new Date()
  const d = new Date(date)
  return d.toDateString() === today.toDateString()
}

// Check if date is in the past
export function isPast(date: Date | string): boolean {
  const today = new Date()
  const d = new Date(date)
  return d < today
}

// Validation functions
export interface ValidationResult {
  isValid: boolean;
  message?: string;
}

// Email validation
export function validateEmail(email: string): ValidationResult {
  if (!email.trim()) {
    return { isValid: false, message: 'Email is required' };
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, message: 'Please enter a valid email address' };
  }
  
  return { isValid: true };
}

// Phone number validation (Canadian format)
export function validatePhoneNumber(phone: string): ValidationResult {
  if (!phone.trim()) {
    return { isValid: false, message: 'Phone number is required' };
  }
  
  // Remove all non-digit characters
  const cleanPhone = phone.replace(/\D/g, '');
  
  // Check if it's a valid Canadian phone number (10 digits)
  if (cleanPhone.length !== 10) {
    return { isValid: false, message: 'Please enter a valid 10-digit phone number' };
  }
  
  return { isValid: true };
}

// Name validation
export function validateName(name: string, fieldName: string): ValidationResult {
  if (!name.trim()) {
    return { isValid: false, message: `${fieldName} is required` };
  }
  
  if (name.trim().length < 2) {
    return { isValid: false, message: `${fieldName} must be at least 2 characters long` };
  }
  
  // Check for valid characters (letters, spaces, hyphens, apostrophes)
  const nameRegex = /^[a-zA-Z\s\-']+$/;
  if (!nameRegex.test(name.trim())) {
    return { isValid: false, message: `${fieldName} can only contain letters, spaces, hyphens, and apostrophes` };
  }
  
  return { isValid: true };
}

// Card number validation
export function validateCardNumber(cardNumber: string): ValidationResult {
  if (!cardNumber.trim()) {
    return { isValid: false, message: 'Card number is required' };
  }
  
  // Remove all non-digit characters
  const cleanCard = cardNumber.replace(/\D/g, '');
  
  // Check if it's a valid card number (13-19 digits)
  if (cleanCard.length < 13 || cleanCard.length > 19) {
    return { isValid: false, message: 'Please enter a valid card number' };
  }
  
  // Luhn algorithm for card validation
  let sum = 0;
  let isEven = false;
  
  for (let i = cleanCard.length - 1; i >= 0; i--) {
    let digit = parseInt(cleanCard[i]);
    
    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    
    sum += digit;
    isEven = !isEven;
  }
  
  if (sum % 10 !== 0) {
    return { isValid: false, message: 'Please enter a valid card number' };
  }
  
  return { isValid: true };
}

// Expiry month validation
export function validateExpiryMonth(month: string): ValidationResult {
  if (!month.trim()) {
    return { isValid: false, message: 'Expiry month is required' };
  }
  
  const monthNum = parseInt(month);
  if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
    return { isValid: false, message: 'Please enter a valid month (01-12)' };
  }
  
  return { isValid: true };
}

// Expiry year validation
export function validateExpiryYear(year: string): ValidationResult {
  if (!year.trim()) {
    return { isValid: false, message: 'Expiry year is required' };
  }
  
  const yearNum = parseInt(year);
  const currentYear = new Date().getFullYear();
  
  if (isNaN(yearNum) || yearNum < currentYear || yearNum > currentYear + 20) {
    return { isValid: false, message: 'Please enter a valid year' };
  }
  
  return { isValid: true };
}

// CVV validation
export function validateCVV(cvv: string): ValidationResult {
  if (!cvv.trim()) {
    return { isValid: false, message: 'CVV is required' };
  }
  
  const cleanCVV = cvv.replace(/\D/g, '');
  if (cleanCVV.length !== 3 && cleanCVV.length !== 4) {
    return { isValid: false, message: 'CVV must be 3 or 4 digits' };
  }
  
  return { isValid: true };
}

// Appointment type validation
export function validateAppointmentType(appointmentType: string): ValidationResult {
  if (!appointmentType.trim()) {
    return { isValid: false, message: 'Appointment type is required' };
  }
  
  return { isValid: true };
}

// Meeting type validation
export function validateMeetingType(meetingType: string): ValidationResult {
  if (!meetingType.trim()) {
    return { isValid: false, message: 'Meeting type is required' };
  }
  
  if (!['online', 'offline'].includes(meetingType)) {
    return { isValid: false, message: 'Please select a valid meeting type' };
  }
  
  return { isValid: true };
}
